import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { afterRenderEffect, Component, DestroyRef, ElementRef, OnInit, viewChild } from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, Validators } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { combineLatest as rxCombineLatest, filter as rxFilter, take as rxTake } from 'rxjs';

import { AnyObject, NavigationService } from '@core';
import { ToastService } from '@messaging/services';

import { WizardStage } from '@login/constants';
import { LoginService } from '@login/services';
import { BasicCompany, LoginType, SAMLSettingsBase, UserLogin } from '@login/types';
import { LoginErrorStateMatcher } from '@login/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'kws-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
  public readonly WizardStage = WizardStage;
  public hidePassword: boolean = true;

  public currentStage = this.WizardStage.Email;
  public rememberMe: boolean = false;
  public matcher = new LoginErrorStateMatcher();
  public userFormGroup: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.pattern('.+@.+\..+')]),
    password: new FormControl('', [Validators.required]),
    companyKey: new FormControl('', [Validators.required]),
  });

  companies: BasicCompany[] = [];
  submitted: boolean = false;

  private cookieName: string = 'rememberMe';
  private readonly emailInput = viewChild<ElementRef<HTMLInputElement>>('emailInput');
  private readonly passwordInput = viewChild<ElementRef<HTMLInputElement>>('passwordInput');
  private readonly companySelect = viewChild(MatSelect);

  get userFormControl() {
    return this.userFormGroup.controls;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private loginService: LoginService,
    private navigationService: NavigationService,
    private toastService: ToastService,
    private destroyRef: DestroyRef,
  ) {
    this.initEmailValidityCheck();

    // `autofocus` only applies on page load, so focus each step's field once it renders.
    // Only one step renders at a time, so at most one of these exists.
    afterRenderEffect(() => {
      const companySelect = this.companySelect();

      if (companySelect) {
        companySelect.focus();
      } else {
        (this.passwordInput() ?? this.emailInput())?.nativeElement.focus();
      }
    });
  }

  ngOnInit() {
    this.checkCookie(false);
    this.checkGenericURL();
  }

  public goBack() {
    switch (this.currentStage) {
      case this.WizardStage.Password:
        this.currentStage = this.WizardStage.Email;
        this.clearFormControl(this.userFormGroup.get('password') as FormControl);
        break;
      case this.WizardStage.Company:
        this.companies = [];
        this.currentStage = this.WizardStage.Password;
        this.clearFormControl(this.userFormGroup.get('companyKey') as FormControl);
        break;
    }
  }

  public beginLogin(): void {
    const emailControl = this.userFormGroup.controls['email'] as FormControl;

    if (emailControl.valid) {
      this.currentStage = this.WizardStage.Password;

      const rememberMeConst = this.checkCookie();
      if (this.rememberMe && rememberMeConst !== emailControl.value) {
        this.setCookie(emailControl.value);
      } else if (!this.rememberMe && rememberMeConst) {
        this.setCookie(emailControl.value, true);
      }

      if (this.userFormGroup.controls['password'].valid) {

        if (this.companies.length) {
          this.currentStage = this.WizardStage.Company;
          const companyError = !this.userFormGroup.controls['companyKey'].value;

          if (!companyError) {
            this.login();
          }
        } else {
          this.submitted = true;
          const postData = this.userFormGroup.value as UserLogin;
          this.loginService.getUserCompanies(postData).subscribe({
            next: (companyList) => {

              if (!companyList || companyList.length === 0) {
                this.handleErrors('Oops! The Email or Password is incorrect. Please try again.');
                return;
              }

              // Single company
              if (companyList.length === 1) {
                this.setCompanyAndLogin(companyList[0].Key);
                return;
              }

              // Multiple companies
              const urlCompanyKey = this.loginService.getCompanyKey().toLowerCase();
              const company = companyList.find(el => el.Key.toLowerCase() === urlCompanyKey);
              if (company) {
                this.setCompanyAndLogin(company.Key);
              } else {
                this.companies = companyList;
                this.currentStage = this.WizardStage.Company;
              }
            },
            error: (error) => {
              this.handleErrors(error);
            },
            complete: () => {
              this.submitted = false;
            },
          });
        }
      }
    }
  }

  private setCompanyAndLogin(companyKey: string) {
    this.userFormGroup.patchValue({ companyKey });
    this.login();
  }

  private checkGenericURL() {
    this.loginService.getSAMLBaseSettings().subscribe({
      next: (settings: SAMLSettingsBase) => {
        if (settings.AuthenticationMode === LoginType.SAML) {
          this.loginService.beginSSO('maui/user');
        }
      },
      error: (e) => {
        this.toastService.queueToastMessage({
          content: 'Unable to retrieve required settings',
          theme: 'error',
        });
      },
    });
  }

  private login(): void {
    this.submitted = true;
    const postData = this.userFormGroup.value as UserLogin;
    rxCombineLatest([
      this.loginService.logIn(postData),
      this.activatedRoute.queryParams,
    ]).pipe(
      rxTake(1),
    )
      .subscribe({
        next: ([loginResp, queryParamMap]: AnyObject[]) => {
          this.toastService.queueToastMessage({
            content: 'You have been successfully logged in.',
            theme: 'success',
          });
          this.navigationService.navigateToLandingPage(queryParamMap);
        },
        error: (error) => {
          this.handleErrors(error);
        },
        complete: () => {
          this.submitted = false;
        },
      });
  }

  private checkCookie(returnValue: boolean = true) {
    const storageVariable = localStorage.getItem(this.cookieName);

    if (storageVariable && storageVariable !== '') {
      if (!returnValue) {
        this.rememberMe = true;
        this.userFormGroup.patchValue({ email: storageVariable });
      } else {
        return storageVariable;
      }
    }
    return null;
  }

  private setCookie(email: string, expire: boolean = false) {
    if (expire) {
      localStorage.removeItem(this.cookieName);
      return;
    }
    localStorage.setItem(this.cookieName, email);
  }

  private clearFormControl(control: FormControl) {
    control.setErrors(null);
    control.setValue('');
    control.updateValueAndValidity();
  }

  /**
   * Subscribes to email control's status changes and resets the `submitted` flag
   * when the email becomes valid. This allows the user to start a new login attempt
   * with a different email address after a previous submission.
   */
  private initEmailValidityCheck(): void {
    this.userFormGroup.controls['email'].statusChanges.pipe(
      rxFilter((status: FormControlStatus) => status === 'VALID'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.submitted = false;
    });
  }

  private handleErrors(error: any) {
    let content = error;

    if (error?.error?.message) {
      content = error.error.message;
    } else if (error instanceof HttpResponse || error instanceof HttpErrorResponse) {
      content = error.statusText;
    } else if (error instanceof Error) {
      content = error.message;
    }

    this.toastService.queueToastMessage({
      content,
      theme: 'error',
    });
  }
}
